import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import ModalBox from "@/shared/components/modal-box/index.vue";
import { TrustAPI } from '@/shared/modules/trust-api';
import { Inject, Options, Prop, Vue, Watch } from "vue-property-decorator";
import { AppInsights } from '@/trust-admin-app/appInsights';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import V3DatePicker from 'vue3-datepicker';
import AlertBox from '@/shared/components/alert-box/index.vue';
import store from '@/trust-admin-app/store';
import { EventEmitter } from 'events';
import debounce from 'lodash/debounce';
import { AccountingAppView, AccountRequest, AccountView, BankBranchRequest, BankBranchView, BankView, PracticeView } from '@/shared/modules/typescript-trustapi-client/models';

@Options({
  components: {
    ModalBox,
    LoadingIndicator,
    ErrorIndicator,
    V3DatePicker,
    AlertBox
  }
})
export default class AddEditModal extends Vue {
  private ai = new AppInsights;

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  @Prop() bankAcct!: AccountView;

  private showAddEditModal!: boolean;
  private showDeleteAlert = false;

  private show = false;
  private isLoadingData = false;
  private errorLoadingData = false;

  private banks: BankView[] = [];
  private accountingApps: AccountingAppView[] = [];
  private formData: AccountView = {};
  private dateOpened: Date = null as any;
  private dateClosed: Date = null as any;
  private selectedAccountingApp: AccountingAppView = null as any;
  private submitting = false;

  private selectedBank: BankBranchView = null as any;
  private isOpen = false;
  private arrowCounter = -1;
  private bankList: BankBranchView[] = [];
  private search = '';
  private isSearching = false;
  private srchDebounced: any = null;

  @Watch('search')
  private onKeywordChange(): void {
    if (this.search.length > 2 && this.bankList.filter(x => x.bankName == this.search).length == 0) {
      this.selectedBank = null as any;
      setTimeout(this.srchDebounced.cancel, 0);
      setTimeout(this.srchDebounced, 0);
    }
  }

  created(): void {
    this.preloadData();
    this.formData = JSON.parse(JSON.stringify(this.bankAcct));

    this.selectedAccountingApp = JSON.parse(JSON.stringify(this.selectedPractice.generalAccountingApp));

    this.dateOpened = this.formData.dateOpened ? new Date(this.formData.dateOpened) : null as any;
    this.dateClosed = this.formData.dateClosed ? new Date(this.formData.dateClosed) : null as any;

    if (this.isEdit) {
      this.selectedBank = {
        bankId: this.bankAcct.bankId,
        bankName: this.bankAcct.bankName,
        branchName: this.bankAcct.bankBranchName,
        bsb: this.bankAcct.bsbno
      };
    }

  }

  mounted(): void {
    window.addEventListener('click', this.handleClickOutside);
    this.srchDebounced = debounce(() => {
      this.searchBank();
    }, 1000);
  }

  beforeUnmount(): void {
    window.removeEventListener('click', this.handleClickOutside);
  }

  private async searchBank(): Promise<void> {
    const _body: BankBranchRequest = {
      keyword: this.search,
      accountTypeId: this.bankAcct.accountType?.id
    }
    try {
      this.isSearching = true;

      const resp: any = await this.apiService.AccountApi?.apiV2AccountGetBankBranchByKeywordSearchPost(_body);
      this.bankList = resp.data.data as BankBranchView[];
      this.isOpen = this.bankList.length > 0;
    } catch (err) {
      this.isOpen = false;
    } finally {
      this.isSearching = false;
    }
  }

  private async saveDetails(): Promise<void> {
    try {
      this.submitting = true;
      const resp: any = this.bankAcct.accountType != null ? await this.apiService.AccountApi?.apiV2AccountSaveAccountPost(
        {
          id: this.formData.id,
          practiceId: this.formData.practiceId,
          accountTypeId: this.formData.accountType.id,
          bsbno: this.selectedBank.bsb,
          bankId: this.selectedBank.bankId,
          bankName: this.selectedBank.bankName,
          bankBranchName: this.selectedBank.branchName,
          accountNo: this.formData.accountNo,
          name: this.formData.name,
          dateOpened: this.dateOpened ? new Date(this.dateOpened) : null as any,
          dateClosed: this.dateClosed ? new Date(this.dateClosed) : null as any
        } as AccountRequest
      ) : await this.apiService.PracticeApi?.apiV2PracticeEditGeneralAccountingAppPost(
        {
          id: this.formData.practiceId,
          generalAccountingAppId: this.selectedAccountingApp.id
        }
      );

      if (!this.bankAcct.accountType) {

        const resp1: any = await this.apiService.PracticeApi?.apiV2PracticeGetPracticeByIdPracticeIdGet(this.selectedPractice.id);

        store.dispatch('setSelectedPractice', resp1.data.data as PracticeView);
        this.eventCtrl.emit('show-snackbar', 'success', this.headingName + ' updated successfully');
      } else {
        this.eventCtrl.emit('reload-accounting-accounts');
        this.eventCtrl.emit('show-snackbar', 'success', 'Bank account ' + (this.isEdit ? 'saved' : 'added') + ' successfully');
      }

      this.closeModal();

    } catch {
      this.eventCtrl.emit('show-snackbar', 'error', 'Problem adding bank account');
    } finally {
      this.submitting = false;
    }
  }

  private async deleteAccount(): Promise<void> {
    try {
      this.submitting = true;
      await this.apiService.AccountApi?.apiV2AccountDeleteAccountAccountIdDelete(
        this.formData.id
      );
      this.eventCtrl.emit('reload-accounting-accounts');
      this.eventCtrl.emit('show-snackbar', 'success', 'Bank account deleted successfully');
      this.closeModal();
    } catch {
      this.eventCtrl.emit('show-snackbar', 'error', 'Problem deleting bank account');
    } finally {
      this.submitting = false;
    }
  }

  private async preloadData(): Promise<void> {
    try {

      this.isLoadingData = true;
      this.errorLoadingData = false;

      const resp: any = await Promise.all([
        this.apiService.ConfigTrustApi?.apiV2ConfigTrustGetBanksPost(),
        this.apiService.AccountingAppApi?.apiV2AccountingAppGetAccountingAppsPost()
      ]);
      this.banks = resp[0].data.data as BankView[];
      this.accountingApps = resp[1].data.data as AccountingAppView[];

    } catch (err) {
      this.errorLoadingData = true;
    } finally {
      this.isLoadingData = false;
    }
  }

  private cancelClicked(): void {
    this.closeModal();
  }

  private closeModal(): void {
    this.$emit('closeModal');
  }

  private deleteBank(): void {
    this.showDeleteAlert = true;
  }

  private yesClicked(): void {
    this.deleteAccount();
  }

  private selectBank(b: BankBranchView): void {
    this.isOpen = false;
    this.search = b.bankName;
    this.selectedBank = b;
  }

  private onArrowDown() {
    if (this.arrowCounter < this.bankList.length) {
      this.arrowCounter = this.arrowCounter + 1;
    }
  }

  private onArrowUp(): void {
    if (this.arrowCounter > 0) {
      this.arrowCounter = this.arrowCounter - 1;
    }
  }

  private onEnter() {
    if (this.bankList.length > 0) {
      this.selectBank(
        this.bankList[this.arrowCounter === -1 ? 0 : this.arrowCounter]
      );
    }
    this.isOpen = false;
  }

  private handleClickOutside(evt: any): void {
    if (this.$el === (evt.target)) {
      this.isOpen = false;
      this.arrowCounter = -1;
    }
  }

  private highlightWord(word: string): string {
    try {
      const regex = new RegExp('(' + this.search + ')', 'ig');
      return word.replace(regex, '<mark>$1</mark>');
    } catch (err) {
      return word;
    }
  }

  get isFormDirty(): boolean {
    return true;
  }

  get validForm(): boolean {
    const _bank: boolean = this.selectedBank !== null; 
    const _accno: boolean = this.formData.accountNo?.trim().length > 0;
    const _accName: boolean = this.formData.name?.trim().length > 0;
    const _dateOpened: boolean = this.dateOpened !== null;
    if (this.bankAcct.accountType?.id == 1 || this.bankAcct.accountType?.id == 2) {
      return _bank && _accno && _accName && _dateOpened ;
    } else {
      return this.selectedAccountingApp !== null;
    }

  }

  get headingName(): string {
    if (this.bankAcct.accountType?.id == 1) {
      return 'trust account';
    } else if (this.bankAcct.accountType?.id == 2) {
      return 'general account';
    } else {
      return 'general accounting system';
    }
  }

  get isEdit(): boolean {
    return this.bankAcct.id > 0 || this.bankAcct?.id == null;
  }

  get selectedPractice(): PracticeView {
    return store.getters.getSelectedPractice;
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }
}

